/**
 * 金額入力欄の3桁カンマ表示。
 *
 * - 素のフォーム欄: <input type="text" inputmode="numeric" data-money name="...">
 *   入力中にカンマ整形し、送信時（formdata イベント）はカンマを除いた値を送る。
 * - Alpine 欄: <input type="text" inputmode="numeric" x-money="item.unit_price">
 *   x-model.number の代わり。表示はカンマ付き、モデルには数値（空欄は null）を書き戻す。
 */

const ALLOWED = /[\d.\-]/;

/** 入力途中の文字列をカンマ付きに整形する（小数・先頭マイナスは保持）。 */
export function formatMoney(value) {
    if (value === null || value === undefined) {
        return '';
    }
    let s = String(value).replace(/[^\d.\-]/g, '');
    const negative = s.startsWith('-');
    s = s.replace(/-/g, '');
    const dot = s.indexOf('.');
    let intPart = dot >= 0 ? s.slice(0, dot) : s;
    const fracPart = dot >= 0 ? s.slice(dot + 1).replace(/\./g, '') : null;
    intPart = intPart.replace(/^0+(?=\d)/, '');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return (negative ? '-' : '') + grouped + (fracPart !== null ? '.' + fracPart : '');
}

/** 保存済みの値（"150000.00" 等）を表示用に整形する。末尾の不要な小数0は落とす。 */
export function formatStoredMoney(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const n = Number(String(value).replace(/,/g, ''));

    return Number.isFinite(n) ? formatMoney(String(n)) : formatMoney(value);
}

/** カンマを除いた送信用の文字列を返す（"-" のみ等は空欄扱い）。 */
export function unformatMoney(value) {
    const s = formatMoney(value).replace(/,/g, '');

    return s === '-' || s === '.' || s === '-.' ? '' : s;
}

/** 数値に変換する（空欄は null）。 */
export function moneyToNumber(value) {
    const s = unformatMoney(value);
    if (s === '') {
        return null;
    }
    const n = parseFloat(s);

    return Number.isFinite(n) ? n : null;
}

/** 入力欄の値をカンマ整形し、キャレットを「左側の数字等の個数」を保つ位置に戻す。 */
function formatInPlace(el) {
    const old = el.value;
    const caret = el.selectionStart ?? old.length;
    let significantBefore = 0;
    for (let i = 0; i < caret; i++) {
        if (ALLOWED.test(old[i])) {
            significantBefore++;
        }
    }
    const next = formatMoney(old);
    if (next === old) {
        return;
    }
    el.value = next;
    let pos = 0;
    let seen = 0;
    while (pos < next.length && seen < significantBefore) {
        if (ALLOWED.test(next[pos])) {
            seen++;
        }
        pos++;
    }
    if (document.activeElement === el) {
        el.setSelectionRange(pos, pos);
    }
}

function formatStoredInPlace(el) {
    if (el._xMoney || el._moneyFormatted) {
        return;
    }
    el._moneyFormatted = true;
    el.value = formatStoredMoney(el.value);
}

document.addEventListener('input', (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.matches('[data-money]') && !el._xMoney) {
        formatInPlace(el);
    }
}, true);

/** 送信データからカンマを除く（画面表示はそのまま）。 */
document.addEventListener('formdata', (e) => {
    e.target.querySelectorAll('input[data-money][name]').forEach((el) => {
        if (!el.disabled && e.formData.has(el.name)) {
            e.formData.set(el.name, unformatMoney(el.value));
        }
    });
});

function formatAll(root = document) {
    root.querySelectorAll?.('input[data-money]').forEach(formatStoredInPlace);
}

document.addEventListener('DOMContentLoaded', () => {
    formatAll();
    new MutationObserver((mutations) => {
        for (const m of mutations) {
            m.addedNodes.forEach((node) => {
                if (node instanceof HTMLInputElement && node.matches('[data-money]')) {
                    formatStoredInPlace(node);
                } else if (node instanceof Element) {
                    formatAll(node);
                }
            });
        }
    }).observe(document.body, { childList: true, subtree: true });
});

export function registerMoneyDirective(Alpine) {
    Alpine.directive('money', (el, { expression }, { evaluateLater, effect, cleanup }) => {
        el._xMoney = true;
        el.setAttribute('data-money', '');
        const getValue = evaluateLater(expression);
        const setValue = evaluateLater(`${expression} = __moneyValue`);

        const onInput = () => {
            formatInPlace(el);
            setValue(() => {}, { scope: { __moneyValue: moneyToNumber(el.value) } });
        };
        el.addEventListener('input', onInput);
        cleanup(() => el.removeEventListener('input', onInput));

        effect(() => getValue((value) => {
            const current = value === '' || value === null || value === undefined ? null : Number(value);
            // 入力中（"1,000." や "-" 等）の表示をモデル反映で潰さない
            if (document.activeElement === el && moneyToNumber(el.value) === current) {
                return;
            }
            el.value = formatStoredMoney(current);
        }));
    }).before('on');
}

window.formatMoney = formatStoredMoney;
window.unformatMoney = unformatMoney;
